import type { Curve } from '../../../../interfaces/toolData';

/**
 * Specialized parser for HopRx ICRUISE files that use hierarchical event-based data format
 * These files contain 'Event' and 'Data' columns where timestamps can be inherited from previous valid entries
 */
export class HopRxParser {
  /**
   * Create curves from HopRx ICRUISE key-value data format
   * @param rawData Parsed CSV rows as key-value objects
   * @param timeValues Array of timestamps (may contain nulls for timestamp inheritance)
   * @param headers CSV header columns
   * @returns Array of curves, one per unique event type
   */
  static createCurves(
    rawData: Record<string, string>[],
    timeValues: (Date | null)[],
    headers: string[]
  ): Curve[] {
    // HopRx ICRUISE uses 'Event' and 'Data' columns instead of telemetry variable/value columns
    const eventCol = headers.find(h => h === 'Event');
    const dataCol = headers.find(h => h === 'Data');
    
    if (!eventCol || !dataCol) {
      throw new Error('HopRx ICRUISE file must have Event and Data columns');
    }
    
    // Group data by event variable, filtering out empty entries
    const eventGroups = new Map<string, Array<{
      timestamp: Date;
      value: number;
      originalIndex: number;
    }>>();
    
    let lastValidTimestamp: Date | null = null;
    
    rawData.forEach((row, index) => {
      const eventName = row[eventCol];
      const valueString = row[dataCol];
      
      // Handle timestamp inheritance FIRST (before data validation)
      let timestamp = timeValues[index];
      
      if (!timestamp || timestamp === null || (timestamp instanceof Date && isNaN(timestamp.getTime()))) {
        if (lastValidTimestamp) {
          timestamp = lastValidTimestamp;
        } else {
          return; // Skip if no valid timestamp available
        }
      } else if (timestamp instanceof Date && !isNaN(timestamp.getTime())) {
        lastValidTimestamp = timestamp;
      }
      
      // Skip empty event names or rows without data
      if (!eventName || eventName.trim() === '' || !valueString || valueString.trim() === '') {
        return;
      }
      
      // Parse the value (skip if not numeric)
      const numericValue = parseFloat(valueString);
      if (isNaN(numericValue)) {
        return;
      }
      
      // Add to the appropriate group
      if (!eventGroups.has(eventName)) {
        eventGroups.set(eventName, []);
      }
      
      eventGroups.get(eventName)!.push({
        timestamp: timestamp as Date,
        value: numericValue,
        originalIndex: index
      });
    });
    
    // Create curves from the grouped data
    const curves: Curve[] = [];
    
    eventGroups.forEach((dataPoints, eventName) => {
      if (dataPoints.length === 0) return;
      
      // Sort by timestamp to ensure proper ordering
      dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      curves.push({
        name: eventName,
        xValues: dataPoints.map(point => point.timestamp),
        yValues: dataPoints.map(point => point.value),
        yUnit: '' // HopRx events typically don't have explicit units
      });
    });
    
    return curves;
  }
}